import { isValidEmail } from "@redotech/email/address";
import type {
  OpensearchBooleanQuery,
  PathAwareQueryContainer,
} from "@redotech/opensearch-types/types";
import { convertArbitraryPhoneNumberToE164 } from "@redotech/phone/number";
import { SPAM_TAG } from "@redotech/redo-support-tags-common/conversation-tag-record";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { getFilterBuilder } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  type ArrayStringFilterData,
  ArrayStringFilterOperator,
} from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import { DateFilterOperator } from "@redotech/redo-views-common/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-views-common/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  decodeFilterGroup,
  encodeFilterGroup,
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "@redotech/redo-views-common/advanced-filters/number-filter";
import { AccountId } from "../ids/definitions";
import { MongoCustomerId } from "../ids/mongo-ids";
import {
  isSupportTicketStatus,
  type SupportTicketSearchHighlightField,
  type SupportTicketStatus,
} from "../models/shopper-ai/conversation/support-ticket";

export const UNASSIGNED_ASSIGNEE_SENTINEL = "UNASSIGNED";
export const AI_AGENT_ASSIGNEE_SENTINEL = "AI_AGENT";

/** Source of truth for support ticket filter names. */
export const supportTicketFilterNames = [
  "status",
  "account",
  "assignees",
  "assigneeType",
  "channels",
  "supportTicketTags",
  "customerTags",
  "mentions",
  "readAt",
  "words",
  "searchContains",
  "wordsSearchFields",
  "searchContainsSearchFields",
  "createdAt",
  "closedAt",
  "lastResponseAt",
  "lastCustomerMessageAt",
  "lastMerchantMessageAt",
  "customerName",
  "customer",
  "participantKeys",
  "primaryParticipantKeys",
  "shopperParticipantIds",
  "merchantUserParticipantIds",
  "redoUserParticipantIds",
  "agentParticipantIds",
  "systemParticipantKeys",
  "shopperAgentConversationIds",
  "numDrafts",
  "numSentMessages",
  "numOutboundMessages",
  "numCustomerMessages",
  "numMerchantSentMessages",
  "numAiSentMessages",
  "numAutoReplySentMessages",
  "numSystemSentMessages",
  "numInternalNotes",
  "inboxEmail",
  "isMergedTicket",
] as const;

/** Valid support ticket filter name. */
export type SupportTicketFilterName = (typeof supportTicketFilterNames)[number];

/** SCREAMING_CASE helpers for callers that prefer enum-style references. */
export const SupportTicketFilterNames = {
  STATUS: "status",
  ACCOUNT: "account",
  ASSIGNEES: "assignees",
  ASSIGNEE_TYPE: "assigneeType",
  CHANNELS: "channels",
  SUPPORT_TICKET_TAGS: "supportTicketTags",
  CUSTOMER_TAGS: "customerTags",
  MENTIONS: "mentions",
  READ_AT: "readAt",
  WORDS: "words",
  SEARCH_CONTAINS: "searchContains",
  WORDS_SEARCH_FIELDS: "wordsSearchFields",
  SEARCH_CONTAINS_SEARCH_FIELDS: "searchContainsSearchFields",
  CREATED_AT: "createdAt",
  CLOSED_AT: "closedAt",
  LAST_RESPONSE_AT: "lastResponseAt",
  LAST_CUSTOMER_MESSAGE_AT: "lastCustomerMessageAt",
  LAST_MERCHANT_MESSAGE_AT: "lastMerchantMessageAt",
  CUSTOMER_NAME: "customerName",
  CUSTOMER: "customer",
  PARTICIPANT_KEYS: "participantKeys",
  PRIMARY_PARTICIPANT_KEYS: "primaryParticipantKeys",
  SHOPPER_PARTICIPANT_IDS: "shopperParticipantIds",
  MERCHANT_USER_PARTICIPANT_IDS: "merchantUserParticipantIds",
  REDO_USER_PARTICIPANT_IDS: "redoUserParticipantIds",
  AGENT_PARTICIPANT_IDS: "agentParticipantIds",
  SYSTEM_PARTICIPANT_KEYS: "systemParticipantKeys",
  SHOPPER_AGENT_CONVERSATION_IDS: "shopperAgentConversationIds",
  NUM_DRAFTS: "numDrafts",
  NUM_SENT_MESSAGES: "numSentMessages",
  NUM_OUTBOUND_MESSAGES: "numOutboundMessages",
  NUM_CUSTOMER_MESSAGES: "numCustomerMessages",
  NUM_MERCHANT_SENT_MESSAGES: "numMerchantSentMessages",
  NUM_AI_SENT_MESSAGES: "numAiSentMessages",
  NUM_AUTO_REPLY_SENT_MESSAGES: "numAutoReplySentMessages",
  NUM_SYSTEM_SENT_MESSAGES: "numSystemSentMessages",
  NUM_INTERNAL_NOTES: "numInternalNotes",
  INBOX_EMAIL: "inboxEmail",
  IS_MERGED_TICKET: "isMergedTicket",
} as const satisfies Record<string, SupportTicketFilterName>;
export type SupportTicketFilterNames =
  (typeof SupportTicketFilterNames)[keyof typeof SupportTicketFilterNames];

const supportTicketFilterNameAliases: Partial<
  Record<string, SupportTicketFilterName>
> = {
  conversationTags: SupportTicketFilterNames.SUPPORT_TICKET_TAGS,
  textConciergeConversationIds:
    SupportTicketFilterNames.SHOPPER_AGENT_CONVERSATION_IDS,
};

export function resolveSupportTicketFilterName(
  name: string,
): SupportTicketFilterName | null {
  const filterName = supportTicketFilterNames.find((n) => n === name);
  return filterName ?? supportTicketFilterNameAliases[name] ?? null;
}

/** Default filter data for each support ticket filter. */
export const supportTicketFilterData = {
  [SupportTicketFilterNames.STATUS]: {
    name: SupportTicketFilterNames.STATUS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.ACCOUNT]: {
    name: SupportTicketFilterNames.ACCOUNT,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.ASSIGNEES]: {
    name: SupportTicketFilterNames.ASSIGNEES,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.ASSIGNEE_TYPE]: {
    name: SupportTicketFilterNames.ASSIGNEE_TYPE,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.CHANNELS]: {
    name: SupportTicketFilterNames.CHANNELS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SUPPORT_TICKET_TAGS]: {
    name: SupportTicketFilterNames.SUPPORT_TICKET_TAGS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.CUSTOMER_TAGS]: {
    name: SupportTicketFilterNames.CUSTOMER_TAGS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.MENTIONS]: {
    name: SupportTicketFilterNames.MENTIONS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.READ_AT]: {
    name: SupportTicketFilterNames.READ_AT,
    type: AdvancedFilterType.EXISTS,
    value: null,
    operator: ExistsFilterOperator.EXISTS,
  },
  [SupportTicketFilterNames.WORDS]: {
    name: SupportTicketFilterNames.WORDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.CONTAINS,
  },
  [SupportTicketFilterNames.SEARCH_CONTAINS]: {
    name: SupportTicketFilterNames.SEARCH_CONTAINS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.CONTAINS,
  },
  [SupportTicketFilterNames.WORDS_SEARCH_FIELDS]: {
    name: SupportTicketFilterNames.WORDS_SEARCH_FIELDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SEARCH_CONTAINS_SEARCH_FIELDS]: {
    name: SupportTicketFilterNames.SEARCH_CONTAINS_SEARCH_FIELDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.CREATED_AT]: {
    name: SupportTicketFilterNames.CREATED_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.CLOSED_AT]: {
    name: SupportTicketFilterNames.CLOSED_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.LAST_RESPONSE_AT]: {
    name: SupportTicketFilterNames.LAST_RESPONSE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.LAST_CUSTOMER_MESSAGE_AT]: {
    name: SupportTicketFilterNames.LAST_CUSTOMER_MESSAGE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.LAST_MERCHANT_MESSAGE_AT]: {
    name: SupportTicketFilterNames.LAST_MERCHANT_MESSAGE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [SupportTicketFilterNames.CUSTOMER_NAME]: {
    name: SupportTicketFilterNames.CUSTOMER_NAME,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.CONTAINS,
  },
  [SupportTicketFilterNames.CUSTOMER]: {
    name: SupportTicketFilterNames.CUSTOMER,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.PARTICIPANT_KEYS]: {
    name: SupportTicketFilterNames.PARTICIPANT_KEYS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.PRIMARY_PARTICIPANT_KEYS]: {
    name: SupportTicketFilterNames.PRIMARY_PARTICIPANT_KEYS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SHOPPER_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.SHOPPER_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.MERCHANT_USER_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.MERCHANT_USER_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.REDO_USER_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.REDO_USER_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.AGENT_PARTICIPANT_IDS]: {
    name: SupportTicketFilterNames.AGENT_PARTICIPANT_IDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SYSTEM_PARTICIPANT_KEYS]: {
    name: SupportTicketFilterNames.SYSTEM_PARTICIPANT_KEYS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.SHOPPER_AGENT_CONVERSATION_IDS]: {
    name: SupportTicketFilterNames.SHOPPER_AGENT_CONVERSATION_IDS,
    type: AdvancedFilterType.EXISTS,
    value: true,
    operator: ExistsFilterOperator.EXISTS,
  },
  [SupportTicketFilterNames.NUM_DRAFTS]: {
    name: SupportTicketFilterNames.NUM_DRAFTS,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_OUTBOUND_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_OUTBOUND_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_CUSTOMER_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_CUSTOMER_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_MERCHANT_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_MERCHANT_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_AI_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_AI_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_AUTO_REPLY_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_AUTO_REPLY_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_SYSTEM_SENT_MESSAGES]: {
    name: SupportTicketFilterNames.NUM_SYSTEM_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.NUM_INTERNAL_NOTES]: {
    name: SupportTicketFilterNames.NUM_INTERNAL_NOTES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [SupportTicketFilterNames.INBOX_EMAIL]: {
    name: SupportTicketFilterNames.INBOX_EMAIL,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [SupportTicketFilterNames.IS_MERGED_TICKET]: {
    name: SupportTicketFilterNames.IS_MERGED_TICKET,
    type: AdvancedFilterType.BOOLEAN,
    value: true,
    operator: BooleanFilterOperator.IS,
  },
} satisfies Record<SupportTicketFilterName, AdvancedFilterData>;

interface SupportTicketFilterPathShape {
  opensearchPath: string;
  keywordPath?: string;
}

export type SupportTicketClientEvaluation = "client" | "server";

/** Filter name to OpenSearch path mapping. */
const supportTicketFilterPathRecord = {
  status: { opensearchPath: "status", keywordPath: "status" },
  account: { opensearchPath: "accountId", keywordPath: "accountId" },
  assignees: { opensearchPath: "assigneeId", keywordPath: "assigneeId" },
  assigneeType: { opensearchPath: "assigneeType", keywordPath: "assigneeType" },
  channels: { opensearchPath: "channels", keywordPath: "channels" },
  supportTicketTags: { opensearchPath: "tags.id", keywordPath: "tags.id" },
  customerTags: {
    opensearchPath: "customerTags",
    keywordPath: "customerTags.keyword",
  },
  mentions: { opensearchPath: "mentions", keywordPath: "mentions" },
  readAt: { opensearchPath: "readAt" },
  words: { opensearchPath: "summary", keywordPath: "summary.keyword" },
  searchContains: { opensearchPath: "summary", keywordPath: "summary.keyword" },
  // Nominal path only: these scope filters emit no OpenSearch clauses of their
  // own (no-op handlers); they configure which fields the searches target.
  wordsSearchFields: { opensearchPath: "summary" },
  searchContainsSearchFields: { opensearchPath: "summary" },
  createdAt: { opensearchPath: "createdAt" },
  closedAt: { opensearchPath: "closedAt" },
  lastResponseAt: { opensearchPath: "lastResponseAt" },
  lastCustomerMessageAt: { opensearchPath: "lastCustomerMessageAt" },
  lastMerchantMessageAt: { opensearchPath: "lastMerchantMessageAt" },
  customerName: {
    opensearchPath: "customerName",
    keywordPath: "customerName.keyword",
  },
  // Values are prefixed `MongoCustomerId`s, matching how the ticket document
  // indexes `mongoCustomerId` (see the order-number clause in addWordsFilter).
  customer: {
    opensearchPath: "mongoCustomerId",
    keywordPath: "mongoCustomerId",
  },
  participantKeys: {
    opensearchPath: "participantKeys",
    keywordPath: "participantKeys",
  },
  primaryParticipantKeys: {
    opensearchPath: "primaryParticipantKeys",
    keywordPath: "primaryParticipantKeys",
  },
  shopperParticipantIds: {
    opensearchPath: "shopperParticipantIds",
    keywordPath: "shopperParticipantIds",
  },
  merchantUserParticipantIds: {
    opensearchPath: "merchantUserParticipantIds",
    keywordPath: "merchantUserParticipantIds",
  },
  redoUserParticipantIds: {
    opensearchPath: "redoUserParticipantIds",
    keywordPath: "redoUserParticipantIds",
  },
  agentParticipantIds: {
    opensearchPath: "agentParticipantIds",
    keywordPath: "agentParticipantIds",
  },
  systemParticipantKeys: {
    opensearchPath: "systemParticipantKeys",
    keywordPath: "systemParticipantKeys",
  },
  shopperAgentConversationIds: {
    opensearchPath: "textConciergeConversationIds",
    keywordPath: "textConciergeConversationIds",
  },
  numDrafts: { opensearchPath: "numDrafts" },
  numSentMessages: { opensearchPath: "numSentMessages" },
  numOutboundMessages: { opensearchPath: "numOutboundMessages" },
  numCustomerMessages: { opensearchPath: "numCustomerMessages" },
  numMerchantSentMessages: { opensearchPath: "numMerchantSentMessages" },
  numAiSentMessages: { opensearchPath: "numAiSentMessages" },
  numAutoReplySentMessages: { opensearchPath: "numAutoReplySentMessages" },
  numSystemSentMessages: { opensearchPath: "numSystemSentMessages" },
  numInternalNotes: { opensearchPath: "numInternalNotes" },
  inboxEmail: { opensearchPath: "inboxEmail", keywordPath: "inboxEmail" },
  isMergedTicket: { opensearchPath: "isMergedTicket" },
} as const satisfies Record<
  SupportTicketFilterName,
  SupportTicketFilterPathShape
>;

type SupportTicketFilterPathValue =
  (typeof supportTicketFilterPathRecord)[SupportTicketFilterName];
type SupportTicketFilterKeywordPath<T> = T extends {
  readonly keywordPath: infer Path extends string;
}
  ? Path
  : never;
type SupportTicketMappedOpensearchPath =
  | SupportTicketFilterPathValue["opensearchPath"]
  | SupportTicketFilterKeywordPath<SupportTicketFilterPathValue>;
/** Plain-analyzed twin of a search field; phrase queries target these. */
export type SupportTicketExactPhrasePath =
  `${SupportTicketSearchHighlightField}.exact`;

type SupportTicketDefaultOpensearchPath =
  | SupportTicketSearchHighlightField
  | SupportTicketExactPhrasePath
  | "accountId"
  | "customerEmail.keyword"
  | "deletedAt"
  | "hasDetachedThread"
  | "kind"
  | "mergedSourceTicketNumbers"
  | "mongoCustomerId"
  | "snoozedUntil"
  | "read"
  | "tags"
  | "tags.name.keyword"
  | "threadCount"
  | "ticketNumber";
export type SupportTicketOpensearchPath =
  | SupportTicketMappedOpensearchPath
  | SupportTicketDefaultOpensearchPath;
export type SupportTicketOpensearchQuery =
  PathAwareQueryContainer<SupportTicketOpensearchPath>;
export type SupportTicketOpensearchBooleanQuery =
  OpensearchBooleanQuery<SupportTicketOpensearchPath>;

/** OpenSearch path mapping for a support ticket filter. */
export interface SupportTicketFilterPath {
  opensearchPath: SupportTicketOpensearchPath;
  keywordPath?: SupportTicketOpensearchPath;
}

/** Gets the OpenSearch path mapping for a support ticket filter. */
export function getSupportTicketFilterPath(
  name: SupportTicketFilterName,
): SupportTicketFilterPath {
  return supportTicketFilterPathRecord[name];
}

const supportTicketClientEvaluationRecord: Record<
  SupportTicketFilterName,
  SupportTicketClientEvaluation
> = {
  status: "client",
  account: "server",
  assignees: "client",
  assigneeType: "server",
  channels: "client",
  supportTicketTags: "client",
  customerTags: "server",
  mentions: "server",
  readAt: "client",
  words: "server",
  searchContains: "server",
  wordsSearchFields: "server",
  searchContainsSearchFields: "server",
  createdAt: "server",
  closedAt: "server",
  lastResponseAt: "server",
  lastCustomerMessageAt: "server",
  lastMerchantMessageAt: "server",
  customerName: "server",
  customer: "server",
  participantKeys: "server",
  primaryParticipantKeys: "server",
  shopperParticipantIds: "server",
  merchantUserParticipantIds: "server",
  redoUserParticipantIds: "server",
  agentParticipantIds: "server",
  systemParticipantKeys: "server",
  shopperAgentConversationIds: "server",
  numDrafts: "client",
  numSentMessages: "client",
  numOutboundMessages: "client",
  numCustomerMessages: "client",
  numMerchantSentMessages: "client",
  numAiSentMessages: "client",
  numAutoReplySentMessages: "client",
  numSystemSentMessages: "client",
  numInternalNotes: "client",
  inboxEmail: "server",
  isMergedTicket: "server",
};

export function getSupportTicketClientEvaluation(
  name: string,
): SupportTicketClientEvaluation | null {
  const filterName = resolveSupportTicketFilterName(name);
  if (!filterName) {
    return null;
  }
  return supportTicketClientEvaluationRecord[filterName];
}

/** Gets default filter data, or undefined for an unknown filter name. */
export function getSupportTicketFilterData(
  name: string,
): AdvancedFilterData | undefined {
  const filterName = resolveSupportTicketFilterName(name);
  if (!filterName) {
    return undefined;
  }
  return supportTicketFilterData[filterName];
}

/**
 * Encodes support-ticket filters for URLs and support-ticket RPC requests.
 */
export function encodeSupportTicketFilterGroup(group: FilterGroup): string {
  return encodeFilterGroup(group, (filter) => {
    const filterBuilder = getFilterBuilder(filter);
    return `${filter.name}@@${filterBuilder.writeToString(filter)}`;
  });
}

/**
 * Decodes support-ticket filters from URLs and support-ticket RPC requests.
 */
export function decodeSupportTicketFilterGroup(
  encoded: string | null | undefined,
): FilterGroup | null {
  if (!encoded) {
    return null;
  }

  return decodeFilterGroup<AdvancedFilterData>(encoded, (filterStr) => {
    const [rawName, dataString] = filterStr.split("@@");
    if (!rawName || dataString === undefined) {
      return null;
    }

    const filterName = resolveSupportTicketFilterName(rawName);
    if (!filterName) {
      return null;
    }

    const defaultFilter = supportTicketFilterData[filterName];
    const filterBuilder = getFilterBuilder(defaultFilter);
    return (
      filterBuilder.readFromString(filterName, dataString, defaultFilter) ??
      null
    );
  });
}

export function getSupportTicketLeafFilters(
  group: FilterGroup,
): AdvancedFilterData[] {
  const filters: AdvancedFilterData[] = [];
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      filters.push(...getSupportTicketLeafFilters(clause));
    } else {
      filters.push(clause);
    }
  }
  return filters;
}

export interface SupportTicketFilterGroupWalker<TContext> {
  createChildContext(parent: TContext, operator: FilterOperator): TContext;
  visitFilter(filter: AdvancedFilterData, context: TContext): void;
  visitGroup(context: TContext, group: FilterGroup): void;
  appendChildContext(parent: TContext, child: TContext): void;
}

export function walkSupportTicketFilterGroup<TContext>(
  group: FilterGroup,
  context: TContext,
  walker: SupportTicketFilterGroupWalker<TContext>,
): void {
  walker.visitGroup(context, group);
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      const childContext = walker.createChildContext(context, clause.operator);
      walkSupportTicketFilterGroup(clause, childContext, walker);
      walker.appendChildContext(context, childContext);
    } else {
      walker.visitFilter(clause, context);
    }
  }
}

function createEmptyBoolQuery(): SupportTicketOpensearchBooleanQuery {
  return { filter: [], must: [], must_not: [], should: [] };
}

const DEFAULT_EXCLUDED_SUPPORT_TICKET_STATUSES = [
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
] as const satisfies readonly SupportTicketStatus[];

function addLifecycleVisibilityExclusions(
  compound: SupportTicketOpensearchBooleanQuery,
  options: {
    includeDeletedAtExclusion: boolean;
    statusExclusions: readonly SupportTicketStatus[];
  },
): void {
  if (options.includeDeletedAtExclusion) {
    compound.must_not.push({ exists: { field: "deletedAt" } });
  }
  if (options.statusExclusions.length > 0) {
    compound.must_not.push({
      terms: { status: [...options.statusExclusions] },
    });
  }
}

function addDefaultSupportTicketExclusions(
  compound: SupportTicketOpensearchBooleanQuery,
  options: {
    includeDeletedAtExclusion: boolean;
    includeSpamExclusion: boolean;
    includeAiAssigneeExclusion: boolean;
    includeMergedTicketExclusion: boolean;
    statusExclusions: readonly SupportTicketStatus[];
  },
): void {
  addLifecycleVisibilityExclusions(compound, options);
  if (options.includeAiAssigneeExclusion) {
    compound.must_not.push({ term: { assigneeType: "AI" } });
  }
  compound.must_not.push({
    bool: {
      filter: [
        { term: { status: "OPEN" } },
        { term: { assigneeType: "CHAT_FLOW" } },
      ],
    },
  });
  if (options.includeSpamExclusion) {
    // SPAM_TAG is the tag name "spam", not an ID, so query tags.name.keyword
    compound.must_not.push({
      nested: {
        path: "tags",
        query: { term: { "tags.name.keyword": SPAM_TAG } },
      },
    });
  }
  compound.must_not.push({
    bool: {
      filter: [
        { term: { threadCount: 0 } },
        { term: { hasDetachedThread: true } },
      ],
    },
  });
  if (options.includeMergedTicketExclusion) {
    compound.must_not.push({ term: { isMergedTicket: true } });
  }
}

function isArrayStringFilter(
  filter: AdvancedFilterData,
): filter is ArrayStringFilterData {
  return filter.type === AdvancedFilterType.ARRAY_TO_STRING;
}

/**
 * Returns true when a leaf filter references the spam tag in any operator
 * (ANY_OF / ALL_OF / NONE_OF). Callers use this to skip the default spam
 * exclusion so views that explicitly target spam — most importantly the Spam
 * view — still return results.
 */
function filterReferencesSpamTag(filter: AdvancedFilterData): boolean {
  const resolvedName = resolveSupportTicketFilterName(filter.name);
  if (resolvedName !== SupportTicketFilterNames.SUPPORT_TICKET_TAGS) {
    return false;
  }
  if (!isArrayStringFilter(filter)) {
    return false;
  }
  return (filter.value ?? []).includes(SPAM_TAG);
}

function filterGroupReferencesSpamTag(group: FilterGroup): boolean {
  return getSupportTicketLeafFilters(group).some(filterReferencesSpamTag);
}

/** Returns true when a leaf filter opts in to AI assignees or all assignee types, suppressing the default AI exclusion. */
function filterReferencesAiAssigneeType(filter: AdvancedFilterData): boolean {
  const resolvedName = resolveSupportTicketFilterName(filter.name);
  if (resolvedName !== SupportTicketFilterNames.ASSIGNEE_TYPE) {
    return false;
  }
  if (!isArrayStringFilter(filter)) {
    return false;
  }
  // Empty NONE_OF means "include all assignee types" — no-op in OpenSearch.
  if (
    filter.operator === ArrayStringFilterOperator.NONE_OF &&
    (filter.value ?? []).length === 0
  ) {
    return true;
  }
  if (filter.operator !== ArrayStringFilterOperator.ANY_OF) {
    return false;
  }
  return (filter.value ?? []).includes("AI");
}

function filterGroupReferencesAiAssigneeType(group: FilterGroup): boolean {
  return getSupportTicketLeafFilters(group).some(
    filterReferencesAiAssigneeType,
  );
}

function filterReferencesIsMergedTicket(filter: AdvancedFilterData): boolean {
  return (
    resolveSupportTicketFilterName(filter.name) ===
    SupportTicketFilterNames.IS_MERGED_TICKET
  );
}

function filterGroupReferencesIsMergedTicket(group: FilterGroup): boolean {
  return getSupportTicketLeafFilters(group).some(
    filterReferencesIsMergedTicket,
  );
}

function getFilterExplicitlyIncludedStatuses(
  filter: AdvancedFilterData,
): Set<SupportTicketStatus> {
  const statuses = new Set<SupportTicketStatus>();
  const resolvedName = resolveSupportTicketFilterName(filter.name);
  if (resolvedName !== SupportTicketFilterNames.STATUS) {
    return statuses;
  }
  if (!isArrayStringFilter(filter)) {
    return statuses;
  }
  if (
    filter.operator !== ArrayStringFilterOperator.ANY_OF &&
    filter.operator !== ArrayStringFilterOperator.ALL_OF
  ) {
    return statuses;
  }
  for (const status of filter.value ?? []) {
    if (isSupportTicketStatus(status)) {
      statuses.add(status);
    }
  }
  return statuses;
}

function addStatuses(
  target: Set<SupportTicketStatus>,
  source: ReadonlySet<SupportTicketStatus>,
): void {
  for (const status of source) {
    target.add(status);
  }
}

function combineStatuses(
  left: ReadonlySet<SupportTicketStatus>,
  right: ReadonlySet<SupportTicketStatus>,
): Set<SupportTicketStatus> {
  const combined = new Set(left);
  addStatuses(combined, right);
  return combined;
}

function getExplicitlyIncludedStatuses(
  filters: readonly AdvancedFilterData[],
): Set<SupportTicketStatus> {
  const statuses = new Set<SupportTicketStatus>();
  for (const filter of filters) {
    addStatuses(statuses, getFilterExplicitlyIncludedStatuses(filter));
  }
  return statuses;
}

function getDefaultExclusionOptions(filters: readonly AdvancedFilterData[]): {
  includeDeletedAtExclusion: boolean;
  statusExclusions: readonly SupportTicketStatus[];
} {
  const explicitlyIncludedStatuses = getExplicitlyIncludedStatuses(filters);
  return {
    includeDeletedAtExclusion: !explicitlyIncludedStatuses.has("DELETED"),
    statusExclusions: DEFAULT_EXCLUDED_SUPPORT_TICKET_STATUSES.filter(
      (status) => !explicitlyIncludedStatuses.has(status),
    ),
  };
}

function getConjunctiveExplicitStatuses(
  group: FilterGroup,
): Set<SupportTicketStatus> {
  const statuses = new Set<SupportTicketStatus>();
  if (group.operator !== FilterOperator.AND) {
    return statuses;
  }
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      addStatuses(statuses, getConjunctiveExplicitStatuses(clause));
    } else {
      addStatuses(statuses, getFilterExplicitlyIncludedStatuses(clause));
    }
  }
  return statuses;
}

interface ScopedLifecycleExclusionOptions {
  readonly includeDeletedAtExclusion: boolean;
  readonly statusExclusions: readonly SupportTicketStatus[];
}

function getScopedLifecycleExclusionOptions(
  topLevelOptions: ScopedLifecycleExclusionOptions,
): ScopedLifecycleExclusionOptions {
  return {
    includeDeletedAtExclusion: !topLevelOptions.includeDeletedAtExclusion,
    statusExclusions: DEFAULT_EXCLUDED_SUPPORT_TICKET_STATUSES.filter(
      (status) => !topLevelOptions.statusExclusions.includes(status),
    ),
  };
}

function addScopedLifecycleExclusions(
  compound: SupportTicketOpensearchBooleanQuery,
  scopedOptions: ScopedLifecycleExclusionOptions,
  explicitlyIncludedStatuses: ReadonlySet<SupportTicketStatus>,
): void {
  const statusExclusions = scopedOptions.statusExclusions.filter(
    (status) => !explicitlyIncludedStatuses.has(status),
  );
  const includeDeletedAtExclusion =
    scopedOptions.includeDeletedAtExclusion &&
    !explicitlyIncludedStatuses.has("DELETED");
  if (!includeDeletedAtExclusion && statusExclusions.length === 0) {
    return;
  }
  addLifecycleVisibilityExclusions(compound, {
    includeDeletedAtExclusion,
    statusExclusions,
  });
}

export const supportTicketWordsSearchFields = [
  "summary",
  "messageContent",
  "emailSubject",
  "customerName",
  "customerEmail",
  "customerPhone",
  "socialHandles",
] as const satisfies readonly SupportTicketSearchHighlightField[];
export type SupportTicketWordsSearchField =
  (typeof supportTicketWordsSearchFields)[number];

export const supportTicketCustomerSearchFields = [
  "customerName",
  "customerEmail",
  "customerPhone",
  "socialHandles",
] as const satisfies readonly SupportTicketSearchHighlightField[];

/**
 * Social-handle field, ranked in its own top tier by the general search so an
 * exact handle match outranks every fuzzy match (see {@link buildSoftWordsClauses}).
 * Social-origin customers (e.g. Facebook comments) carry their name here rather
 * than in `customerName`, so an exact hit must not be diluted to the fuzzy tier.
 */
export const supportTicketSocialHandleSearchFields = [
  "socialHandles",
] as const satisfies readonly SupportTicketSearchHighlightField[];

/**
 * The non-customer ("content") subset of {@link supportTicketWordsSearchFields}.
 * The general search ranks customer-identity matches above these (see
 * {@link buildSoftWordsClauses}).
 */
export const supportTicketContentSearchFields = [
  "summary",
  "messageContent",
  "emailSubject",
] as const satisfies readonly SupportTicketSearchHighlightField[];

/** Field keys a merchant can scope the search to. */
export const supportTicketSearchFieldKeys = [
  "customerName",
  "customerEmail",
  "customerPhone",
  "socialHandles",
  "emailSubject",
  "messageContent",
  "summary",
  "ticketNumber",
  "orderNumber",
] as const;
export type SupportTicketSearchFieldKey =
  (typeof supportTicketSearchFieldKeys)[number];

export const defaultSupportTicketSearchFieldKeys =
  supportTicketSearchFieldKeys.filter((key) => key !== "summary");

/** Resolved words/searchContains field scope; null means "search every field". */
export interface ResolvedSearchFieldScope {
  readonly customerFields: readonly SupportTicketSearchHighlightField[];
  readonly contentFields: readonly SupportTicketSearchHighlightField[];
  readonly includeSocialExact: boolean;
  readonly includeEmailExact: boolean;
  readonly includeTicketNumber: boolean;
  readonly includeOrderNumber: boolean;
}

function isSupportTicketSearchFieldKey(
  value: string,
): value is SupportTicketSearchFieldKey {
  return (supportTicketSearchFieldKeys as readonly string[]).includes(value);
}

function resolveSearchFieldScope(
  keys: ReadonlySet<SupportTicketSearchFieldKey>,
): ResolvedSearchFieldScope {
  return {
    customerFields: supportTicketCustomerSearchFields.filter((f) =>
      keys.has(f),
    ),
    contentFields: supportTicketContentSearchFields.filter((f) => keys.has(f)),
    includeSocialExact: keys.has("socialHandles"),
    includeEmailExact: keys.has("customerEmail"),
    includeTicketNumber: keys.has("ticketNumber"),
    includeOrderNumber: keys.has("orderNumber"),
  };
}

const scopeFilterNameBySearch: Partial<
  Record<SupportTicketFilterName, SupportTicketFilterName>
> = {
  [SupportTicketFilterNames.WORDS]:
    SupportTicketFilterNames.WORDS_SEARCH_FIELDS,
  [SupportTicketFilterNames.SEARCH_CONTAINS]:
    SupportTicketFilterNames.SEARCH_CONTAINS_SEARCH_FIELDS,
};

/** Scope-leaf filter name that stores the field selection for a search filter. */
export function getSupportTicketScopeFilterName(
  searchFilterName: SupportTicketFilterName,
): SupportTicketFilterName | null {
  return scopeFilterNameBySearch[searchFilterName] ?? null;
}

/** Resolves the field scope for a search filter; empty/absent → null (all fields). */
export function getSupportTicketSearchFieldScope(
  filterGroup: FilterGroup | null | undefined,
  searchFilterName: SupportTicketFilterName,
): ResolvedSearchFieldScope | null {
  if (!filterGroup) {
    return null;
  }
  const scopeFilterName = getSupportTicketScopeFilterName(searchFilterName);
  if (!scopeFilterName) {
    return null;
  }
  const leaf = getSupportTicketLeafFilters(filterGroup).find(
    (f) => resolveSupportTicketFilterName(f.name) === scopeFilterName,
  );
  if (!leaf || !isArrayStringFilter(leaf)) {
    return null;
  }
  const keys = new Set(
    (leaf.value ?? []).filter((v): v is SupportTicketSearchFieldKey =>
      typeof v === "string" ? isSupportTicketSearchFieldKey(v) : false,
    ),
  );
  if (keys.size === 0) {
    return null;
  }
  return resolveSearchFieldScope(keys);
}

type CustomerSearchKind = "email" | "phone" | "name";

/**
 * Classifies a customer search term by its shape so the query targets only the
 * relevant identity fields. A bare name no longer matches against email/phone
 * (and vice versa), which is the core "filtering hardening" fix: without this,
 * searching "John Smith" leaks into any email fragment or phone digit run, and
 * a name term OR-matches every customer named John or Smith.
 *
 * Mirrors the input-typing dispatch used by the legacy customer search
 * (redo/deprecated-helpdesk) and the global customer search
 * (search-customers-opensearch).
 */
function classifyCustomerSearchTerm(term: string): CustomerSearchKind {
  if (term.includes("@") || isValidEmail(term)) {
    return "email";
  }
  // Require at least one digit, not just a non-null E.164: a "+"-prefixed term
  // with no digits ("+vip") sanitizes to "+", which `convertArbitraryPhoneNumberToE164`
  // returns as a (truthy) E.164. Without the digit guard that term classifies as
  // "phone" but yields zero phone candidates, so the customer filter would be
  // dropped entirely (returning all tickets). The guard routes it to "name".
  if (/\d/.test(term) && convertArbitraryPhoneNumberToE164(term) !== null) {
    return "phone";
  }
  return "name";
}

const customerSearchFieldsByKind = {
  email: ["customerEmail"],
  phone: ["customerPhone"],
  name: ["customerName", "socialHandles"],
} as const satisfies Record<
  CustomerSearchKind,
  readonly SupportTicketSearchHighlightField[]
>;

/**
 * Identity fields searched/highlighted for a given customer search term, scoped
 * by {@link classifyCustomerSearchTerm}.
 */
function getCustomerSearchFieldsForTerm(
  term: string,
): readonly SupportTicketSearchHighlightField[] {
  return customerSearchFieldsByKind[classifyCustomerSearchTerm(term)];
}

/**
 * Exact phone digit tokens for a free-form phone term. `customerPhone` stores
 * space-joined E.164 numbers tokenized by the standard analyzer (the leading
 * "+" is dropped), so each stored number is a single digit token whose country
 * code is a prefix ("+1 555 123 4567" → "15551234567"). These candidates are
 * matched as whole tokens, so they only hit a *fully-qualified* stored number:
 * the raw digits as entered (covers international input typed with a country
 * code) and the normalized E.164 digits. National numbers typed without a
 * country code are handled separately, country-agnostically, by the trailing
 * wildcard in {@link buildCustomerSearchClauses}.
 */
function getCustomerPhoneSearchDigits(term: string): string[] {
  const candidates = new Set<string>();
  const onlyDigits = term.replace(/\D/g, "");
  if (onlyDigits) {
    candidates.add(onlyDigits);
  }
  const e164 = convertArbitraryPhoneNumberToE164(term);
  if (e164) {
    const e164Digits = e164.replace(/\D/g, "");
    if (e164Digits) {
      candidates.add(e164Digits);
    }
  }
  return [...candidates];
}

/** True when a free-text term should be searched against email addresses. */
function termIsEmailShaped(term: string): boolean {
  return classifyCustomerSearchTerm(term) === "email";
}

// Keeps a typed `*`/`?` from globbing in a wildcard query.
function escapeOpensearchWildcard(term: string): string {
  return term.replace(/[\\*?]/g, "\\$&");
}

// Matches a full email by re-tokenizing to alphanumeric parts the edge_ngram
// index stores. Drops tokens < 2 chars (below min_gram) so operator:"and" doesn't zero out.
function buildCustomerEmailMatchClause(
  term: string,
): SupportTicketOpensearchQuery | null {
  const tokens = term
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .split(/\s+/)
    .filter((t) => t.length >= 2);
  if (tokens.length === 0) {
    return null;
  }
  return {
    match: { customerEmail: { query: tokens.join(" "), operator: "and" } },
  };
}

/**
 * OpenSearch clauses for a customer search term, scoped to the fields that
 * match the term's shape. Name/social use `operator: "and"` so every token must
 * match (no more matching every "John"); phone is digit-normalized; email is
 * matched as a single edge-ngram unit.
 *
 * Clauses carry relevance boosts so the strongest matches float to the top once
 * `_score` ordering is active (see {@link filterGroupHasCustomerSearch}): an
 * exact email outranks a looser edge-ngram match, and a name match requiring all
 * tokens outranks the social-handle fallback. Boosts only affect score, never
 * membership — every branch still requires `minimum_should_match: 1`.
 */
function buildCustomerSearchClauses(
  term: string,
): SupportTicketOpensearchQuery[] {
  const kind = classifyCustomerSearchTerm(term);

  if (kind === "phone") {
    const clauses: SupportTicketOpensearchQuery[] =
      getCustomerPhoneSearchDigits(term).map((digits) => ({
        // Whole-token match on a fully-qualified number; boosted so an exact hit
        // ranks above the partial suffix match below.
        match: { customerPhone: { query: digits, boost: 2 } },
      }));
    // Country-agnostic national-number match. The stored token is the E.164
    // number with its country code as a prefix, so digits typed without a
    // country code are a suffix of it ("5551234567" → "15551234567";
    // "7911123456" → "447911123456"). A trailing wildcard matches that suffix
    // for any country, replacing the old "1"-prefix guess that only worked for
    // US/NANP numbers.
    const onlyDigits = term.replace(/\D/g, "");
    if (onlyDigits) {
      clauses.push({
        wildcard: { customerPhone: { value: `*${onlyDigits}` } },
      });
    }
    return clauses;
  }

  if (kind === "email") {
    const matchClause = buildCustomerEmailMatchClause(term);
    return [
      // Exact (case-folded) email beats a partial edge-ngram match.
      {
        term: {
          "customerEmail.keyword": { value: term.toLowerCase(), boost: 4 },
        },
      },
      ...(matchClause != null ? [matchClause] : []),
    ];
  }

  return [
    // `customerName` is indexed with an edge_ngram tokenizer, so match_phrase
    // can't reliably enforce adjacency on a multi-word name (the per-word
    // edge_ngram tokens land at non-adjacent positions). Require all tokens via
    // `operator: "and"` and boost the name field above the social-handle fallback.
    { match: { customerName: { query: term, operator: "and", boost: 2 } } },
    { match: { socialHandles: { query: term, operator: "and" } } },
  ];
}

export interface SupportTicketSearchHighlightTerm {
  searchTerm: string;
  fields: readonly SupportTicketSearchHighlightField[];
}

function isIntegerString(value: string): boolean {
  return /^\d+$/.test(value);
}

export function parseTicketNumber(searchTerm: string): number | null {
  const normalized = searchTerm.startsWith("#")
    ? searchTerm.slice(1)
    : searchTerm;
  return isIntegerString(normalized) ? Number(normalized) : null;
}

export function getSupportTicketWordsSearchTerm(
  filter: AdvancedFilterData,
): string | null {
  if (!isArrayStringFilter(filter)) {
    return null;
  }

  const searchTerm = (filter.value?.[0] as string | undefined)
    ?.substring(0, 200)
    .trim();
  return searchTerm || null;
}

export function getSupportTicketPositiveWordsSearchTerm(
  filterGroup: FilterGroup | null | undefined,
): string | null {
  return (
    getSupportTicketSearchHighlightTerms(filterGroup).find((term) =>
      term.fields.includes("summary"),
    )?.searchTerm ?? null
  );
}

export function getSupportTicketSearchHighlightTerms(
  filterGroup: FilterGroup | null | undefined,
): SupportTicketSearchHighlightTerm[] {
  if (!filterGroup) {
    return [];
  }

  return getSupportTicketLeafFilters(filterGroup).flatMap(
    (filter): SupportTicketSearchHighlightTerm[] => {
      if (!isArrayStringFilter(filter)) {
        return [];
      }
      if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
        return [];
      }

      if (filter.name === SupportTicketFilterNames.SEARCH_CONTAINS) {
        return (filter.value ?? []).flatMap((value) => {
          const term = (value as string | undefined)?.substring(0, 200).trim();
          if (!term) {
            return [];
          }
          return [
            {
              searchTerm: term,
              fields: getSupportTicketSearchHighlightFields(
                filterGroup,
                SupportTicketFilterNames.SEARCH_CONTAINS,
              ),
            },
          ];
        });
      }

      const searchTerm = getSupportTicketWordsSearchTerm(filter);
      if (!searchTerm) {
        return [];
      }

      if (filter.name === SupportTicketFilterNames.WORDS) {
        return [
          {
            searchTerm,
            fields: getSupportTicketSearchHighlightFields(
              filterGroup,
              SupportTicketFilterNames.WORDS,
            ),
          },
        ];
      }
      if (filter.name === SupportTicketFilterNames.CUSTOMER_NAME) {
        return [
          { searchTerm, fields: getCustomerSearchFieldsForTerm(searchTerm) },
        ];
      }

      return [];
    },
  );
}

function getSupportTicketSearchHighlightFields(
  filterGroup: FilterGroup,
  searchFilterName:
    | typeof SupportTicketFilterNames.WORDS
    | typeof SupportTicketFilterNames.SEARCH_CONTAINS,
): readonly SupportTicketSearchHighlightField[] {
  const scope = getSupportTicketSearchFieldScope(filterGroup, searchFilterName);
  if (scope === null) {
    return supportTicketWordsSearchFields;
  }

  const scopedFields = new Set([
    ...scope.contentFields,
    ...scope.customerFields,
  ]);
  return supportTicketWordsSearchFields.filter((field) =>
    scopedFields.has(field),
  );
}

// Clauses for a highlight term's `highlight_query`, tokenizing each field like
// retrieval so the highlighted span matches (email → alphanumeric ngram parts).
export function buildSupportTicketHighlightClauses(
  term: SupportTicketSearchHighlightTerm,
): SupportTicketOpensearchQuery[] {
  return term.fields.flatMap((field): SupportTicketOpensearchQuery[] => {
    if (field === "customerEmail" && termIsEmailShaped(term.searchTerm)) {
      const clause = buildCustomerEmailMatchClause(term.searchTerm);
      return clause != null ? [clause] : [];
    }
    if (
      field === "customerPhone" &&
      classifyCustomerSearchTerm(term.searchTerm) === "phone"
    ) {
      return getCustomerPhoneSearchDigits(term.searchTerm).map((digits) => ({
        match: { customerPhone: digits },
      }));
    }
    return [{ match: { [field]: term.searchTerm } }];
  });
}

/**
 * True when the filter group contains an active (CONTAINS) search of one of
 * `names` with a non-empty term. NOT_CONTAINS is an exclusion, not a search, so
 * it never counts.
 */
function filterGroupHasActiveSearch(
  filterGroup: FilterGroup | null | undefined,
  names: ReadonlySet<SupportTicketFilterName>,
): boolean {
  if (!filterGroup) {
    return false;
  }
  return getSupportTicketLeafFilters(filterGroup).some((filter) => {
    const resolved = resolveSupportTicketFilterName(filter.name);
    if (resolved === null || !names.has(resolved)) {
      return false;
    }
    if (!isArrayStringFilter(filter)) {
      return false;
    }
    if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
      return false;
    }
    return Boolean(getSupportTicketWordsSearchTerm(filter));
  });
}

const customerSearchFilterNames: ReadonlySet<SupportTicketFilterName> = new Set(
  [SupportTicketFilterNames.CUSTOMER_NAME],
);

const relevanceSearchFilterNames: ReadonlySet<SupportTicketFilterName> =
  new Set([
    SupportTicketFilterNames.WORDS,
    SupportTicketFilterNames.SEARCH_CONTAINS,
    SupportTicketFilterNames.CUSTOMER_NAME,
  ]);

/**
 * True when the filter group contains an active (CONTAINS) customer search with
 * a non-empty term.
 */
export function filterGroupHasCustomerSearch(
  filterGroup: FilterGroup | null | undefined,
): boolean {
  return filterGroupHasActiveSearch(filterGroup, customerSearchFilterNames);
}

/**
 * True when the filter group contains an active (CONTAINS) free-text search —
 * either the general search bar ({@link SupportTicketFilterNames.WORDS}) or the
 * dedicated customer search. Callers use this to switch result ordering to
 * relevance (`_score` first) so the best match floats to the top instead of
 * being buried by more-recently-updated tickets.
 */
export function filterGroupHasRelevanceSearch(
  filterGroup: FilterGroup | null | undefined,
): boolean {
  return filterGroupHasActiveSearch(filterGroup, relevanceSearchFilterNames);
}

/**
 * `#1234`-style ticket-number clauses for a free-text term. A bare integer or
 * `#`-prefixed integer matches the ticket's own number or any merged source
 * ticket number. Returns `[]` when the term isn't a ticket number.
 */
function buildWordsTicketNumberClauses(
  searchTerm: string,
): SupportTicketOpensearchQuery[] {
  const ticketNumber = parseTicketNumber(searchTerm);
  if (ticketNumber === null) {
    return [];
  }
  return [
    { term: { ticketNumber } },
    { term: { mergedSourceTicketNumbers: ticketNumber } },
  ];
}

/**
 * Soft-search only: ranges that make a numeric term act as a ticket-number
 * prefix. `ticketNumber` is mapped `long`, so `prefix` is unavailable.
 */
function buildWordsTicketNumberPrefixClauses(
  searchTerm: string,
): SupportTicketOpensearchQuery[] {
  const ticketNumber = parseTicketNumber(searchTerm);
  if (ticketNumber === null) {
    return [];
  }
  const clauses: SupportTicketOpensearchQuery[] = [];
  for (let magnitude = 10; ; magnitude *= 10) {
    const gte = ticketNumber * magnitude;
    const lte = gte + magnitude - 1;
    if (!Number.isSafeInteger(gte) || !Number.isSafeInteger(lte)) {
      break;
    }
    clauses.push(
      { range: { ticketNumber: { gte, lte } } },
      { range: { mergedSourceTicketNumbers: { gte, lte } } },
    );
  }
  return clauses;
}

/**
 * Strict cross-field clauses for the general search: a token must actually
 * occur in one of the searched fields. Used for NOT_CONTAINS exclusion (where
 * fuzzy/prefix widening would wrongly exclude unrelated near-misses) and as the
 * exact-match backbone of the soft search.
 */
function buildStrictWordsClauses(
  searchTerm: string,
  scope?: ResolvedSearchFieldScope | null,
): SupportTicketOpensearchQuery[] {
  const wordFields = scope
    ? [...scope.contentFields, ...scope.customerFields]
    : supportTicketWordsSearchFields;
  const includeEmailExact = scope ? scope.includeEmailExact : true;
  const includeTicketNumber = scope ? scope.includeTicketNumber : true;
  return [
    ...wordFields.map((f) => ({ match: { [f]: searchTerm } })),
    ...(includeEmailExact && termIsEmailShaped(searchTerm)
      ? (() => {
          const clause = buildCustomerEmailMatchClause(searchTerm);
          return clause != null ? [clause] : [];
        })()
      : []),
    ...(includeTicketNumber ? buildWordsTicketNumberClauses(searchTerm) : []),
  ];
}

/** Order-number search: `mongoCustomerId` is the order->conversation join key. */
function buildOrderCustomerClauses(
  orderResolvedCustomerIds: readonly string[] | undefined,
  { includeOrderNumber }: { includeOrderNumber: boolean },
): SupportTicketOpensearchQuery[] {
  if (
    !includeOrderNumber ||
    !orderResolvedCustomerIds ||
    orderResolvedCustomerIds.length === 0
  ) {
    return [];
  }
  return [
    {
      constant_score: {
        filter: {
          terms: {
            ["mongoCustomerId"]: orderResolvedCustomerIds.map(
              (id) => MongoCustomerId.fromString(id) as string,
            ),
          },
        },
        boost: WORDS_TIER_BOOST.customerExact,
      },
    },
  ];
}

/** "Matches exactly": the term must occur as one contiguous, in-order phrase. */
function buildExactPhraseWordsClauses(
  searchTerm: string,
  scope?: ResolvedSearchFieldScope | null,
): SupportTicketOpensearchQuery[] {
  const wordFields = scope
    ? [...scope.contentFields, ...scope.customerFields]
    : supportTicketWordsSearchFields;
  const includeTicketNumber = scope ? scope.includeTicketNumber : true;
  return [
    // `.exact`, not the parent: edge_ngram prefix tokens occupy consecutive
    // positions, so match_phrase on the parent can never span two words.
    ...wordFields.map(
      (f): SupportTicketOpensearchQuery => ({
        match_phrase: { [`${f}.exact`]: searchTerm },
      }),
    ),
    /** Identity joins are exact by construction; keep `#1234` searchable. */
    ...(includeTicketNumber ? buildWordsTicketNumberClauses(searchTerm) : []),
  ];
}

/**
 * Strict tier boosts for the general search. Each tier is wrapped in
 * `constant_score` so its contribution is fixed (independent of term frequency),
 * and the weights are spaced so every tier outranks the *sum* of all lower
 * tiers (accounting for overlap — an exact hit also matches its column's fuzzy
 * clause). Exact-vs-fuzzy is the primary axis, customer-vs-content secondary:
 *
 *   social-handle-exact (8) + customer-exact (8) = 16 (exact handle)
 *     > customer-exact (8+2=10) > content-exact (4+1=5)
 *     > customer-fuzzy (2) > content-fuzzy (1)
 *
 * So an exact match always beats a fuzzy one: a customer whose name only
 * fuzzy-matches (or doesn't match) ranks BELOW a ticket whose summary/body
 * exactly contains the term. `socialHandles` sits in both the social tier and
 * {@link supportTicketCustomerSearchFields}, so an exact handle hit scores in
 * both exact clauses (8+8) and leads the customer tier — social customers (e.g.
 * Facebook comments) keep their name in `socialHandles`, so an exact hit clears
 * every fuzzy tier. Recency breaks ties within a tier.
 */
const WORDS_TIER_BOOST = {
  socialHandlesExact: 8,
  customerExact: 8,
  contentExact: 4,
  customerFuzzy: 2,
  contentFuzzy: 1,
} as const;

/** Exact-token clause: every query token must be present (no fuzziness). */
function buildExactFieldsClause(
  searchTerm: string,
  fields: readonly SupportTicketSearchHighlightField[],
  boost: number,
): SupportTicketOpensearchQuery {
  return {
    constant_score: {
      filter: {
        multi_match: {
          query: searchTerm,
          fields: [...fields],
          type: "best_fields",
          operator: "and",
        },
      },
      boost,
    },
  };
}

/** Fuzzy/prefix clause: typo tolerance (`fuzziness: AUTO`) plus as-you-type. */
function buildFuzzyFieldsClause(
  searchTerm: string,
  fields: readonly SupportTicketSearchHighlightField[],
  boost: number,
): SupportTicketOpensearchQuery {
  return {
    constant_score: {
      filter: {
        bool: {
          should: [
            {
              multi_match: {
                query: searchTerm,
                fields: [...fields],
                type: "best_fields",
                fuzziness: "AUTO",
                prefix_length: 1,
              },
            },
            {
              multi_match: {
                query: searchTerm,
                fields: [...fields],
                type: "phrase_prefix",
              },
            },
          ],
          minimum_should_match: 1,
        },
      },
      boost,
    },
  };
}

/**
 * Token-count ceiling above which the fuzzy/prefix tier of the general search is
 * dropped. The fuzzy tier (`fuzziness: AUTO` + `phrase_prefix`) rewrites every
 * query token into the set of index terms within edit distance, OR'd across all
 * searched fields. For a few words that's cheap, but a long pasted sentence
 * multiplies tokens × fields × fuzzy expansions and overruns Lucene's
 * `maxClauseCount` (1024), which OpenSearch rejects with
 * `too_many_nested_clauses` — surfacing to the user as a 500 on support search.
 * Past this many tokens we keep only the bounded exact tiers: someone pasting a
 * whole sentence wants an exact substring match, not typo tolerance, so this
 * also sharpens relevance. The exact tier is a plain per-token `multi_match`
 * (tokens × fields clauses, no fuzzy fan-out), which stays well under the limit.
 */
export const SUPPORT_TICKET_WORDS_FUZZY_MAX_TOKENS = 16;

/** Whitespace-delimited token count of a (already trimmed) search term. */
function countSearchTokens(searchTerm: string): number {
  const trimmed = searchTerm.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
}

/**
 * Long pasted phrases must use the exact-phrase query path rather than the
 * fuzzy/prefix search path, which can exceed OpenSearch's clause limit.
 */
export function shouldUseExactSupportTicketWordsSearch(
  searchTerm: string,
): boolean {
  return countSearchTokens(searchTerm) > SUPPORT_TICKET_WORDS_FUZZY_MAX_TOKENS;
}

/**
 * Soft cross-field clauses for the general search bar. Widens the result set to
 * typo'd and partially-typed near-misses while ranking matches in a strict
 * priority once `_score` ordering is active (see
 * {@link filterGroupHasRelevanceSearch}). Exact matches always beat fuzzy ones;
 * an exact social-handle hit leads outright, then the customer column wins
 * within the same exactness:
 *
 *   1. social-handle field, exact              ({@link supportTicketSocialHandleSearchFields})
 *   2. customer-identity fields, exact         ({@link supportTicketCustomerSearchFields})
 *   3. other ("content") fields, exact         ({@link supportTicketContentSearchFields})
 *   4. customer-identity fields, fuzzy/prefix  (dropped past {@link SUPPORT_TICKET_WORDS_FUZZY_MAX_TOKENS} tokens)
 *   5. other ("content") fields, fuzzy/prefix  (dropped past {@link SUPPORT_TICKET_WORDS_FUZZY_MAX_TOKENS} tokens)
 *
 * See {@link WORDS_TIER_BOOST} for how the constant-score tiering guarantees
 * this ordering. A ticket-number hit (`#1234`) is treated as a customer-exact
 * match (a numeric term never collides with a handle search). Boosts only
 * affect score, never membership — `minimum_should_match: 1` still requires at
 * least one clause to match. Long phrases drop the fuzzy tier (see
 * {@link SUPPORT_TICKET_WORDS_FUZZY_MAX_TOKENS}) so the query can't blow Lucene's clause limit.
 * Purely numeric queries (bare integers or `#`-prefixed integers) also drop the
 * fuzzy tier so that e.g. "91092" does not fuzzy-match near-miss order numbers
 * like "91022" in email subject text.
 */
function buildSoftWordsClauses(
  searchTerm: string,
  scope?: ResolvedSearchFieldScope | null,
): SupportTicketOpensearchQuery[] {
  const customerFields = scope
    ? scope.customerFields
    : supportTicketCustomerSearchFields;
  const contentFields = scope
    ? scope.contentFields
    : supportTicketContentSearchFields;
  const socialFields: readonly SupportTicketSearchHighlightField[] = (
    scope ? scope.includeSocialExact : true
  )
    ? supportTicketSocialHandleSearchFields
    : [];
  const includeEmailExact = scope ? scope.includeEmailExact : true;
  const includeTicketNumber = scope ? scope.includeTicketNumber : true;

  const includeFuzzyTier =
    !shouldUseExactSupportTicketWordsSearch(searchTerm) &&
    parseTicketNumber(searchTerm) === null;

  const emailMatchClause =
    includeEmailExact && termIsEmailShaped(searchTerm)
      ? buildCustomerEmailMatchClause(searchTerm)
      : null;

  const clauses: SupportTicketOpensearchQuery[] = [];

  if (includeEmailExact && termIsEmailShaped(searchTerm)) {
    // Exact keyword match — bypasses edge_ngram token-length limits entirely.
    // customerEmail.keyword already exists in the index with folded_lowercase.
    clauses.push({
      constant_score: {
        filter: {
          term: {
            "customerEmail.keyword": { value: searchTerm.toLowerCase() },
          },
        },
        boost: WORDS_TIER_BOOST.customerExact,
      },
    });
    // customerEmail is a space-joined blob of every address on the ticket, so
    // `term` only fires when it holds exactly one. Substring-match the rest.
    clauses.push({
      constant_score: {
        filter: {
          wildcard: {
            "customerEmail.keyword": {
              value: `*${escapeOpensearchWildcard(searchTerm.toLowerCase())}*`,
            },
          },
        },
        boost: WORDS_TIER_BOOST.customerExact,
      },
    });
    // Edge-ngram partial match for prefix/as-you-type search.
    if (emailMatchClause != null) {
      clauses.push({
        constant_score: {
          filter: emailMatchClause,
          boost: WORDS_TIER_BOOST.customerExact,
        },
      });
    }
  }

  if (socialFields.length > 0) {
    clauses.push(
      buildExactFieldsClause(
        searchTerm,
        socialFields,
        WORDS_TIER_BOOST.socialHandlesExact,
      ),
    );
  }
  if (customerFields.length > 0) {
    clauses.push(
      buildExactFieldsClause(
        searchTerm,
        customerFields,
        WORDS_TIER_BOOST.customerExact,
      ),
    );
  }
  if (contentFields.length > 0) {
    clauses.push(
      buildExactFieldsClause(
        searchTerm,
        contentFields,
        WORDS_TIER_BOOST.contentExact,
      ),
    );
  }

  if (includeFuzzyTier) {
    if (customerFields.length > 0) {
      clauses.push(
        buildFuzzyFieldsClause(
          searchTerm,
          customerFields,
          WORDS_TIER_BOOST.customerFuzzy,
        ),
      );
    }
    if (contentFields.length > 0) {
      clauses.push(
        buildFuzzyFieldsClause(
          searchTerm,
          contentFields,
          WORDS_TIER_BOOST.contentFuzzy,
        ),
      );
    }
  }

  if (includeTicketNumber) {
    for (const clause of [
      ...buildWordsTicketNumberClauses(searchTerm),
      ...buildWordsTicketNumberPrefixClauses(searchTerm),
    ]) {
      clauses.push({
        constant_score: {
          filter: clause,
          boost: WORDS_TIER_BOOST.customerExact,
        },
      });
    }
  }

  return clauses;
}

function addWordsFilter(
  filter: ArrayStringFilterData,
  compound: SupportTicketOpensearchBooleanQuery,
  orderResolvedCustomerIds?: readonly string[],
  scope?: ResolvedSearchFieldScope | null,
): void {
  const searchTerm = getSupportTicketWordsSearchTerm(filter);
  if (!searchTerm) {
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
    compound.must_not.push({
      bool: {
        should: buildStrictWordsClauses(searchTerm, scope),
        minimum_should_match: 1,
      },
    });
    return;
  }

  const includeOrderNumber = scope ? scope.includeOrderNumber : true;

  if (
    filter.operator === ArrayStringFilterOperator.MATCHES_EXACTLY ||
    shouldUseExactSupportTicketWordsSearch(searchTerm)
  ) {
    const phraseClauses = buildExactPhraseWordsClauses(searchTerm, scope);
    compound.filter.push({
      bool: {
        should: [
          ...phraseClauses,
          ...buildOrderCustomerClauses(orderResolvedCustomerIds, {
            includeOrderNumber,
          }),
        ],
        minimum_should_match: 1,
      },
    });
    return;
  }

  // `must` keeps per-clause boosts in `_score`; membership matches filter context.
  compound.must.push({
    bool: {
      should: [
        ...buildSoftWordsClauses(searchTerm, scope),
        ...buildOrderCustomerClauses(orderResolvedCustomerIds, {
          includeOrderNumber,
        }),
      ],
      minimum_should_match: 1,
    },
  });
}

function addCustomerSearchFilter(
  filter: ArrayStringFilterData,
  compound: SupportTicketOpensearchBooleanQuery,
): void {
  const searchTerm = getSupportTicketWordsSearchTerm(filter);
  if (!searchTerm) {
    return;
  }

  const clauses = buildCustomerSearchClauses(searchTerm);
  if (clauses.length === 0) {
    return;
  }

  const multiFieldQuery = {
    bool: { should: clauses, minimum_should_match: 1 },
  } satisfies SupportTicketOpensearchQuery;

  if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
    compound.must_not.push(multiFieldQuery);
  } else {
    // `must` (not `filter`) so the per-field boosts contribute to `_score` and
    // the best match can be sorted to the top. Membership is identical to
    // filter context.
    compound.must.push(multiFieldQuery);
  }
}

function addSupportTicketTagsFilter(
  filter: ArrayStringFilterData,
  path: SupportTicketFilterPath,
  compound: SupportTicketOpensearchBooleanQuery,
): void {
  const tagValues = filter.value ?? [];
  if (tagValues.length === 0) {
    return;
  }

  // SPAM_TAG is a tag name ("spam"), not an ID. When filtering only on the
  // spam tag, query tags.name.keyword instead of tags.id.
  const isSpamTagOnly = tagValues.length === 1 && tagValues[0] === SPAM_TAG;
  const fieldPath = isSpamTagOnly ? "tags.name.keyword" : getKeywordPath(path);

  if (filter.operator === ArrayStringFilterOperator.ALL_OF) {
    // ALL_OF: Every specified tag must be present
    for (const value of tagValues) {
      compound.filter.push({
        nested: { path: "tags", query: { term: { [fieldPath]: value } } },
      });
    }
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
    // ANY_OF: At least one of the specified tags must be present
    compound.filter.push({
      nested: {
        path: "tags",
        query: { bool: { filter: [{ terms: { [fieldPath]: tagValues } }] } },
      },
    });
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
    // NONE_OF: None of the specified tags should be present
    // The must_not must be at the outer level, wrapping the nested query
    compound.must_not.push({
      nested: {
        path: "tags",
        query: { bool: { filter: [{ terms: { [fieldPath]: tagValues } }] } },
      },
    });
    return;
  }
}

export interface SupportTicketOpensearchQueryOptions {
  includeAiAssigneeExclusion?: boolean;
  /** False lets a caller (the All view) opt out of the default spam exclusion. */
  includeSpamExclusion?: boolean;
  /** False lets a caller opt out of the default merged-ticket exclusion. */
  includeMergedTicketExclusion?: boolean;
  timeZoneId?: string | null;
  // Redo customer mongo oids (hex) resolved from an order number in the search
  // term; matched against `mongoCustomerId`. See {@link addWordsFilter}.
  orderResolvedCustomerIds?: readonly string[];
  // Resolved field scope for the `words` / `searchContains` searches. Null/absent
  // ⇒ search every field. See {@link getSupportTicketSearchFieldScope}.
  searchFields?: ResolvedSearchFieldScope | null;
  searchContainsSearchFields?: ResolvedSearchFieldScope | null;
}

interface SupportTicketOpensearchFilterHandlerInput {
  filter: AdvancedFilterData;
  path: SupportTicketFilterPath;
  compound: SupportTicketOpensearchBooleanQuery;
  options?: SupportTicketOpensearchQueryOptions;
}

type SupportTicketOpensearchFilterHandler = (
  input: SupportTicketOpensearchFilterHandlerInput,
) => void;

function getKeywordPath(
  path: SupportTicketFilterPath,
): SupportTicketOpensearchPath {
  return path.keywordPath ?? path.opensearchPath;
}

const genericOpensearchFilterHandler: SupportTicketOpensearchFilterHandler = ({
  filter,
  path,
  compound,
  options,
}) => {
  getFilterBuilder(filter).addOpensearchFilters({
    filter,
    opensearchPath: path.opensearchPath,
    keywordPath: path.keywordPath,
    compound,
    timeZoneId: options?.timeZoneId ?? null,
  });
};

const wordsOpensearchFilterHandler: SupportTicketOpensearchFilterHandler = ({
  filter,
  compound,
  options,
}) => {
  if (!isArrayStringFilter(filter)) {
    return;
  }
  addWordsFilter(
    filter,
    compound,
    options?.orderResolvedCustomerIds,
    options?.searchFields,
  );
};

const searchContainsOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  ({ filter, compound, options }) => {
    if (!isArrayStringFilter(filter)) {
      return;
    }
    for (const value of filter.value ?? []) {
      const trimmed = (value as string | undefined)?.substring(0, 200).trim();
      if (!trimmed) {
        continue;
      }
      addWordsFilter(
        { ...filter, value: [trimmed] },
        compound,
        undefined,
        options?.searchContainsSearchFields,
      );
    }
  };

/** Scope filters emit no query; they narrow the search field set via options. */
const noopOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  () => {};

const customerNameOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  ({ filter, compound }) => {
    if (!isArrayStringFilter(filter)) {
      return;
    }
    addCustomerSearchFilter(filter, compound);
  };

const supportTicketTagsOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  ({ filter, path, compound }) => {
    if (!isArrayStringFilter(filter)) {
      return;
    }
    addSupportTicketTagsFilter(filter, path, compound);
  };

const assigneesOpensearchFilterHandler: SupportTicketOpensearchFilterHandler =
  ({ filter, path, compound }) => {
    if (!isArrayStringFilter(filter)) {
      return;
    }

    const values = filter.value ?? [];
    if (values.length === 0) {
      return;
    }

    const sentinelValues = values.filter(
      (v) => v === UNASSIGNED_ASSIGNEE_SENTINEL,
    );
    const aiSentinelValues = values.filter(
      (v) => v === AI_AGENT_ASSIGNEE_SENTINEL,
    );
    const userValues = values.filter(
      (v) =>
        v !== UNASSIGNED_ASSIGNEE_SENTINEL && v !== AI_AGENT_ASSIGNEE_SENTINEL,
    );

    const hasSentinel = sentinelValues.length > 0;
    const hasAiSentinel = aiSentinelValues.length > 0;
    const hasUsers = userValues.length > 0;
    const keywordPath = getKeywordPath(path);

    if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
      const shouldClauses: PathAwareQueryContainer<SupportTicketOpensearchPath>[] =
        [];
      if (hasSentinel) {
        shouldClauses.push({
          bool: { must_not: [{ exists: { field: path.opensearchPath } }] },
        });
      }
      if (hasAiSentinel) {
        shouldClauses.push({ term: { assigneeType: "AI" } });
      }
      if (hasUsers) {
        shouldClauses.push({ terms: { [keywordPath]: userValues } });
      }
      if (shouldClauses.length === 0) {
        return;
      }
      if (shouldClauses.length === 1) {
        if (hasSentinel) {
          compound.must_not.push({ exists: { field: path.opensearchPath } });
        } else if (hasAiSentinel) {
          compound.filter.push({ term: { assigneeType: "AI" } });
        } else {
          compound.filter.push({ terms: { [keywordPath]: userValues } });
        }
      } else {
        compound.filter.push({
          bool: { should: shouldClauses, minimum_should_match: 1 },
        });
      }
      return;
    }

    if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
      if (hasSentinel) {
        compound.must.push({ exists: { field: path.opensearchPath } });
      }
      if (hasAiSentinel) {
        compound.must_not.push({ term: { assigneeType: "AI" } });
      }
      if (hasUsers) {
        compound.must_not.push({ terms: { [keywordPath]: userValues } });
      }
      return;
    }
  };

function createNotFutureSnoozedClause(
  nowIso: string,
): SupportTicketOpensearchQuery {
  return {
    bool: {
      should: [
        { bool: { must_not: [{ exists: { field: "snoozedUntil" } }] } },
        { range: { snoozedUntil: { lte: nowIso } } },
      ],
      minimum_should_match: 1,
    },
  };
}

function createFutureSnoozedClause(
  nowIso: string,
): SupportTicketOpensearchQuery {
  return { range: { snoozedUntil: { gt: nowIso } } };
}

function createEffectiveStatusClause(
  status: SupportTicketStatus,
  nowIso: string,
): SupportTicketOpensearchQuery {
  switch (status) {
    case "OPEN":
      return {
        bool: {
          filter: [
            { term: { status: "OPEN" } },
            createNotFutureSnoozedClause(nowIso),
          ],
        },
      };
    case "SNOOZED":
      return {
        bool: {
          should: [
            { term: { status: "SNOOZED" } },
            {
              bool: {
                filter: [
                  { term: { status: "OPEN" } },
                  createFutureSnoozedClause(nowIso),
                ],
              },
            },
          ],
          minimum_should_match: 1,
        },
      };
    case "CLOSED":
    case "IN_PROGRESS":
    case "HIDDEN":
    case "ARCHIVED":
    case "DELETED":
    case "DRAFT":
      return { term: { status } };
  }
  const _exhaustive: never = status;
  return _exhaustive;
}

function createStatusClause(
  status: string,
  nowIso: string,
): SupportTicketOpensearchQuery {
  return isSupportTicketStatus(status)
    ? createEffectiveStatusClause(status, nowIso)
    : { match_none: {} };
}

const statusOpensearchFilterHandler: SupportTicketOpensearchFilterHandler = ({
  filter,
  compound,
}) => {
  if (!isArrayStringFilter(filter)) {
    return;
  }

  const values = filter.value ?? [];
  if (values.length === 0) {
    return;
  }

  const nowIso = new Date().toISOString();
  const statusClauses = values.map((status) =>
    createStatusClause(status, nowIso),
  );

  if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
    if (statusClauses.length === 1) {
      const [statusClause] = statusClauses;
      if (statusClause) {
        compound.filter.push(statusClause);
      }
      return;
    }
    compound.filter.push({
      bool: { should: statusClauses, minimum_should_match: 1 },
    });
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
    compound.must_not.push({
      bool: { should: statusClauses, minimum_should_match: 1 },
    });
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.ALL_OF) {
    compound.filter.push(...statusClauses);
  }
};

// Filters on the indexed `read` boolean (isSupportTicketReadForIndex) so
// counts match the rendered rows; pre-`read` docs fall back to supersets.
const readAtOpensearchFilterHandler: SupportTicketOpensearchFilterHandler = ({
  filter,
  path,
  compound,
}) => {
  if (filter.type !== AdvancedFilterType.EXISTS) {
    return;
  }
  const { value, operator } = filter;
  if (value == null) {
    return;
  }

  // EXISTS+true / NOT_EXISTS+false select read; the inverse select unread.
  const selectsRead = operator === ExistsFilterOperator.EXISTS ? value : !value;
  const lastCustomerMessageAtPath =
    supportTicketFilterPathRecord.lastCustomerMessageAt.opensearchPath;
  const missingReadField: SupportTicketOpensearchQuery = {
    bool: { must_not: [{ exists: { field: "read" } }] },
  };

  if (selectsRead) {
    // Fallback superset: read ⊆ (readAt exists OR no customer message).
    compound.filter.push({
      bool: {
        should: [
          { term: { read: true } },
          {
            bool: {
              filter: [missingReadField],
              should: [
                { exists: { field: path.opensearchPath } },
                {
                  bool: {
                    must_not: [
                      { exists: { field: lastCustomerMessageAtPath } },
                    ],
                  },
                },
              ],
              minimum_should_match: 1,
            },
          },
        ],
        minimum_should_match: 1,
      },
    });
  } else {
    // Fallback superset: unread ⊆ (a customer message exists).
    compound.filter.push({
      bool: {
        should: [
          { term: { read: false } },
          {
            bool: {
              filter: [
                missingReadField,
                { exists: { field: lastCustomerMessageAtPath } },
              ],
            },
          },
        ],
        minimum_should_match: 1,
      },
    });
  }
};

const supportTicketOpensearchFilterHandlers = {
  status: statusOpensearchFilterHandler,
  account: genericOpensearchFilterHandler,
  assignees: assigneesOpensearchFilterHandler,
  assigneeType: genericOpensearchFilterHandler,
  channels: genericOpensearchFilterHandler,
  supportTicketTags: supportTicketTagsOpensearchFilterHandler,
  customerTags: genericOpensearchFilterHandler,
  mentions: genericOpensearchFilterHandler,
  readAt: readAtOpensearchFilterHandler,
  words: wordsOpensearchFilterHandler,
  searchContains: searchContainsOpensearchFilterHandler,
  wordsSearchFields: noopOpensearchFilterHandler,
  searchContainsSearchFields: noopOpensearchFilterHandler,
  createdAt: genericOpensearchFilterHandler,
  closedAt: genericOpensearchFilterHandler,
  lastResponseAt: genericOpensearchFilterHandler,
  lastCustomerMessageAt: genericOpensearchFilterHandler,
  lastMerchantMessageAt: genericOpensearchFilterHandler,
  customerName: customerNameOpensearchFilterHandler,
  customer: genericOpensearchFilterHandler,
  participantKeys: genericOpensearchFilterHandler,
  primaryParticipantKeys: genericOpensearchFilterHandler,
  shopperParticipantIds: genericOpensearchFilterHandler,
  merchantUserParticipantIds: genericOpensearchFilterHandler,
  redoUserParticipantIds: genericOpensearchFilterHandler,
  agentParticipantIds: genericOpensearchFilterHandler,
  systemParticipantKeys: genericOpensearchFilterHandler,
  shopperAgentConversationIds: genericOpensearchFilterHandler,
  numDrafts: genericOpensearchFilterHandler,
  numSentMessages: genericOpensearchFilterHandler,
  numOutboundMessages: genericOpensearchFilterHandler,
  numCustomerMessages: genericOpensearchFilterHandler,
  numMerchantSentMessages: genericOpensearchFilterHandler,
  numAiSentMessages: genericOpensearchFilterHandler,
  numAutoReplySentMessages: genericOpensearchFilterHandler,
  numSystemSentMessages: genericOpensearchFilterHandler,
  numInternalNotes: genericOpensearchFilterHandler,
  inboxEmail: genericOpensearchFilterHandler,
  isMergedTicket: genericOpensearchFilterHandler,
} satisfies Record<
  SupportTicketFilterName,
  SupportTicketOpensearchFilterHandler
>;

/**
 * Adds a single filter to an OpenSearch boolean query using the generic filter builder.
 */
export function addSupportTicketFilterToOpensearch(
  filter: AdvancedFilterData,
  compound: SupportTicketOpensearchBooleanQuery,
  options?: SupportTicketOpensearchQueryOptions,
): void {
  const filterName = resolveSupportTicketFilterName(filter.name);
  if (!filterName) {
    return;
  }

  const path = getSupportTicketFilterPath(filterName);
  supportTicketOpensearchFilterHandlers[filterName]({
    filter,
    path,
    compound,
    options,
  });
}

/**
 * Processes a filter group recursively, adding filters to the compound query.
 * Handles nested AND/OR groups by creating appropriate nested bool queries.
 */
function processFilterGroup(
  group: FilterGroup,
  compound: SupportTicketOpensearchBooleanQuery,
  options?: SupportTicketOpensearchQueryOptions,
  scopedLifecycleOptions?: ScopedLifecycleExclusionOptions,
): void {
  interface OpensearchWalkContext {
    compound: SupportTicketOpensearchBooleanQuery;
    inheritedStatuses: ReadonlySet<SupportTicketStatus>;
  }

  const processGroup = (
    currentGroup: FilterGroup,
    context: OpensearchWalkContext,
  ): void => {
    const groupStatuses =
      currentGroup.operator === FilterOperator.AND
        ? combineStatuses(
            context.inheritedStatuses,
            getConjunctiveExplicitStatuses(currentGroup),
          )
        : context.inheritedStatuses;
    const groupContext = { ...context, inheritedStatuses: groupStatuses };

    if (currentGroup.operator === FilterOperator.OR) {
      groupContext.compound.minimum_should_match =
        (groupContext.compound.minimum_should_match ?? 0) === 0
          ? 1
          : groupContext.compound.minimum_should_match;
    }

    for (const clause of currentGroup.clauses) {
      if (isFilterGroup(clause)) {
        const childCompound = createEmptyBoolQuery();
        processGroup(clause, {
          compound: childCompound,
          inheritedStatuses: groupContext.inheritedStatuses,
        });
        if (currentGroup.operator === FilterOperator.AND) {
          groupContext.compound.filter.push({ bool: childCompound });
        } else {
          if (clause.operator !== FilterOperator.OR && scopedLifecycleOptions) {
            addScopedLifecycleExclusions(
              childCompound,
              scopedLifecycleOptions,
              combineStatuses(
                groupContext.inheritedStatuses,
                getConjunctiveExplicitStatuses(clause),
              ),
            );
          }
          groupContext.compound.should.push({ bool: childCompound });
        }
        continue;
      }

      if (currentGroup.operator === FilterOperator.AND) {
        addSupportTicketFilterToOpensearch(
          clause,
          groupContext.compound,
          options,
        );
      } else {
        const clauseCompound = createEmptyBoolQuery();
        addSupportTicketFilterToOpensearch(clause, clauseCompound, options);
        if (scopedLifecycleOptions) {
          addScopedLifecycleExclusions(
            clauseCompound,
            scopedLifecycleOptions,
            combineStatuses(
              groupContext.inheritedStatuses,
              getFilterExplicitlyIncludedStatuses(clause),
            ),
          );
        }
        groupContext.compound.should.push({ bool: clauseCompound });
      }
    }
  };

  processGroup(group, { compound, inheritedStatuses: new Set() });
}

/**
 * Builds an OpenSearch boolean query from a filter group.
 * Supports nested AND/OR filter groups.
 * By default, excludes soft-deleted and AI-assigned support tickets.
 */
export function buildSupportTicketOpensearchQuery(
  accountId: AccountId,
  filterGroup: FilterGroup | null | undefined,
  options?: SupportTicketOpensearchQueryOptions,
): SupportTicketOpensearchBooleanQuery {
  return buildSupportTicketOpensearchQueryAcrossAccounts(
    [accountId],
    filterGroup,
    options,
  );
}

/**
 * Multi-account variant of `buildSupportTicketOpensearchQuery`. When the
 * caller's same-email user is on multiple connected teams and has the
 * required permission on more than one of them, the support-ticket list must
 * include rows from each. Emits a `terms` filter on `accountId` instead of
 * `term` so a single OpenSearch query covers every allowed account.
 */
export function buildSupportTicketOpensearchQueryAcrossAccounts(
  accountIds: readonly AccountId[],
  filterGroup: FilterGroup | null | undefined,
  options?: SupportTicketOpensearchQueryOptions,
): SupportTicketOpensearchBooleanQuery {
  const compound = createEmptyBoolQuery();

  // Always filter by team and product kind.
  compound.filter.push({ terms: { accountId: [...accountIds] } });
  // Keep support-ticket search isolated from AskRedo and merchant-support docs.
  compound.filter.push({ term: { kind: "shopper_support" } });

  const leafFilters = filterGroup
    ? getSupportTicketLeafFilters(filterGroup)
    : [];
  const includeSpamExclusion =
    (options?.includeSpamExclusion ?? true) &&
    (!filterGroup || !filterGroupReferencesSpamTag(filterGroup));
  const includeAiAssigneeExclusion =
    (options?.includeAiAssigneeExclusion ?? true) &&
    (!filterGroup || !filterGroupReferencesAiAssigneeType(filterGroup));
  const includeMergedTicketExclusion =
    (options?.includeMergedTicketExclusion ?? true) &&
    (!filterGroup || !filterGroupReferencesIsMergedTicket(filterGroup));
  const lifecycleExclusionOptions = getDefaultExclusionOptions(leafFilters);
  addDefaultSupportTicketExclusions(compound, {
    includeSpamExclusion,
    includeAiAssigneeExclusion,
    includeMergedTicketExclusion,
    ...lifecycleExclusionOptions,
  });

  if (filterGroup) {
    processFilterGroup(
      filterGroup,
      compound,
      options,
      getScopedLifecycleExclusionOptions(lifecycleExclusionOptions),
    );
  }

  return compound;
}

/**
 * Builds an OpenSearch boolean query from a flat array of filters.
 * All filters are combined with AND logic.
 * By default, excludes soft-deleted and AI-assigned support tickets.
 */
export function buildSupportTicketOpensearchQueryFromFilters(
  accountId: AccountId,
  filters: AdvancedFilterData[],
  options?: {
    includeAiAssigneeExclusion?: boolean;
    includeSpamExclusion?: boolean;
    includeMergedTicketExclusion?: boolean;
    timeZoneId?: string | null;
  },
): SupportTicketOpensearchBooleanQuery {
  const compound = createEmptyBoolQuery();

  // Always filter by accountId and product kind.
  compound.filter.push({ term: { accountId } });
  compound.filter.push({ term: { kind: "shopper_support" } });

  const includeSpamExclusion =
    !filters.some(filterReferencesSpamTag) &&
    (options?.includeSpamExclusion ?? true);
  const includeAiAssigneeExclusion =
    !filters.some(filterReferencesAiAssigneeType) &&
    (options?.includeAiAssigneeExclusion ?? true);
  const includeMergedTicketExclusion =
    !filters.some(filterReferencesIsMergedTicket) &&
    (options?.includeMergedTicketExclusion ?? true);
  addDefaultSupportTicketExclusions(compound, {
    includeSpamExclusion,
    includeAiAssigneeExclusion,
    includeMergedTicketExclusion,
    ...getDefaultExclusionOptions(filters),
  });

  for (const filter of filters) {
    addSupportTicketFilterToOpensearch(filter, compound, options);
  }

  return compound;
}
